import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import {
  clearAllSessionTokens,
  clearStoredSessionToken,
  currentPortalFromPath,
  getStoredSessionToken,
  storeSessionToken,
} from '../services/sessionTokens';
import { tenantSlugFromHost, tenantSlugFromPathOrHost } from '../utils/tenantDomains';

const AuthContext = createContext(null);
const LOCAL_DEMO_TOKEN = 'demo_staff_chat';
const LOCAL_PLAYER_DEMO_TOKEN = 'demo_player_chat';
const LOCAL_DEMO_EMAIL = 'demo@white.room';
const LOCAL_PLAYER_DEMO_EMAIL = 'player@white.room';
const LOCAL_DEMO_PASSWORD = 'DemoChat2026!';
const LOCAL_DEMO_PASSWORD_SHORT = 'demo';

const isLocalDemoHost = () => (
  typeof window !== 'undefined'
  && ['127.0.0.1', 'localhost'].includes(window.location.hostname)
);

const isDemoTenantAccess = () => (
  isLocalDemoHost()
  || resolveTenantSlug() === 'demo'
);

const setDemoSessionToken = (token) => {
  storeSessionToken(token, { tenant: 'demo', portal: currentPortalFromPath(), sessionOnly: true });
};

const localDemoUser = {
  id: 'demo-staff-chat',
  username: 'White Room Demo',
  email: LOCAL_DEMO_EMAIL,
  role: 'admin',
  tenant_id: 'demo-tenant',
  tenant_slug: 'demo',
  tenant_name: 'White Room Demo',
  is_master_admin: true,
  permissions: ['chat:respond'],
};

const localPlayerDemoUser = {
  id: 'demo-player-1',
  username: 'Emily Demo',
  email: LOCAL_PLAYER_DEMO_EMAIL,
  role: 'player',
  tenant_id: 'demo-tenant',
  tenant_slug: 'demo',
  tenant_name: 'White Room Demo',
  balance: 52,
};

const resolveTenantSlug = () => {
  return tenantSlugFromPathOrHost();
};

const normalizeTenantKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .replace(/\/.*$/, '')
  .replace(/\.$/, '');

const addTenantAlias = (aliases, value) => {
  const normalized = normalizeTenantKey(value);
  if (!normalized) return;
  aliases.add(normalized);
  if (!normalized.includes('.')) aliases.add(`${normalized}.playemp.vip`);
};

const collectTenantAliases = (authUser = {}) => {
  const aliases = new Set();
  [
    authUser.tenant_slug,
    authUser.tenant_domain,
    authUser.tenant_custom_domain,
    authUser.tenant_agent_domain,
    authUser.custom_domain,
    authUser.customDomain,
    authUser.canonical_domain,
    authUser.canonicalDomain,
    authUser.domain,
    authUser.host,
    authUser.tenant?.slug,
    authUser.tenant?.custom_domain,
    authUser.tenant?.customDomain,
    authUser.tenant?.canonical_domain,
    authUser.tenant?.agent_domain,
    authUser.tenant?.agentDomain,
    authUser.tenant?.agent_canonical_domain,
  ].forEach((value) => addTenantAlias(aliases, value));

  [
    authUser.tenant_domains,
    authUser.tenant_domain_aliases,
    authUser.tenant_agent_domains,
    authUser.custom_domains,
    authUser.domains,
    authUser.domain_aliases,
    authUser.tenant?.custom_domains,
    authUser.tenant?.domains,
    authUser.tenant?.domain_aliases,
  ].forEach((values) => {
    if (Array.isArray(values)) values.forEach((value) => addTenantAlias(aliases, value));
  });
  return aliases;
};

const sessionMatchesCurrentTenant = (authUser, currentTenant) => {
  const current = normalizeTenantKey(currentTenant);
  if (!current || !authUser?.tenant_id) return true;
  const allowed = collectTenantAliases(authUser);
  return allowed.has(current);
};

const storeSessionForTenantAliases = (token, {
  canonicalTenant,
  currentTenant,
  portal,
  sessionOnly = false,
} = {}) => {
  if (!token) return;
  const tenants = new Set();
  const canonical = normalizeTenantKey(canonicalTenant);
  const current = normalizeTenantKey(currentTenant);
  const hostTenant = normalizeTenantKey(tenantSlugFromHost());
  if (canonical) tenants.add(canonical);
  if (current) tenants.add(current);
  if (hostTenant) tenants.add(hostTenant);
  if (tenants.size === 0) tenants.add(resolveTenantSlug() || 'main');
  tenants.forEach((tenant) => {
    storeSessionToken(token, { tenant, portal, sessionOnly });
  });
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState(resolveTenantSlug());

  useEffect(() => {
    const resolved = resolveTenantSlug();
    if (resolved) setTenantSlug(resolved);
    checkAuth();
  }, []);

  const checkAuth = async () => {
    let attemptedToken = null;
    try {
      const token = getStoredSessionToken();
      attemptedToken = token;
      if (!token) { setUser(null); setLoading(false); return; }
      if (isDemoTenantAccess() && token === LOCAL_DEMO_TOKEN) {
        setTenantSlug('demo');
        setUser(localDemoUser);
        setLoading(false);
        return;
      }
      if (isDemoTenantAccess() && token === LOCAL_PLAYER_DEMO_TOKEN) {
        setTenantSlug('demo');
        setUser(localPlayerDemoUser);
        setLoading(false);
        return;
      }
      const res = await api.get('/api/auth/me');
      const currentTenant = resolveTenantSlug();
      if (!sessionMatchesCurrentTenant(res.data, currentTenant)) {
        clearStoredSessionToken({ token });
        setUser(null);
        setLoading(false);
        return;
      }
      if (token) {
        storeSessionForTenantAliases(token, {
          canonicalTenant: res.data?.tenant_slug || tenantSlug,
          currentTenant,
          portal: currentPortalFromPath(),
        });
      }
      if (res.data?.tenant_slug) setTenantSlug(res.data.tenant_slug);
      setUser(res.data);
    } catch {
      clearStoredSessionToken({ token: attemptedToken });
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password, tenant = null, portal = null, options = {}) => {
    const loginPortal = portal || currentPortalFromPath();
    clearStoredSessionToken({ tenant: tenant || tenantSlug, portal: loginPortal });
    setUser(null);
    if (
      isDemoTenantAccess()
      && String(identifier || '').trim().toLowerCase() === LOCAL_DEMO_EMAIL
      && [LOCAL_DEMO_PASSWORD, LOCAL_DEMO_PASSWORD_SHORT].includes(password)
    ) {
      setDemoSessionToken(LOCAL_DEMO_TOKEN);
      setTenantSlug('demo');
      setUser(localDemoUser);
      return { token: LOCAL_DEMO_TOKEN, user: localDemoUser };
    }
    if (
      isDemoTenantAccess()
      && String(identifier || '').trim().toLowerCase() === LOCAL_PLAYER_DEMO_EMAIL
      && [LOCAL_DEMO_PASSWORD, LOCAL_DEMO_PASSWORD_SHORT].includes(password)
    ) {
      setDemoSessionToken(LOCAL_PLAYER_DEMO_TOKEN);
      setTenantSlug('demo');
      setUser(localPlayerDemoUser);
      return { token: LOCAL_PLAYER_DEMO_TOKEN, user: localPlayerDemoUser };
    }
    const loginTenant = options.staffChat ? (options.tenantSlug || tenant || null) : (tenant || tenantSlug);
    const res = await api.post('/api/auth/login', {
      identifier,
      password,
      tenant_slug: loginTenant || undefined,
      portal: portal || undefined,
      staff_chat: Boolean(options.staffChat),
      turnstile_token: options.turnstileToken || undefined,
    });
    const token = res.data.token || res.data.session_id;
    const nextTenant = res.data.user?.tenant_slug || tenant || tenantSlug;
    if (token) {
      storeSessionForTenantAliases(token, {
        canonicalTenant: nextTenant,
        currentTenant: tenant || resolveTenantSlug() || tenantSlug,
        portal: loginPortal,
      });
    }
    if (res.data.user?.tenant_slug) setTenantSlug(res.data.user.tenant_slug);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, fullName, username = null, referralCode = null, tenant = null) => {
    clearStoredSessionToken({ tenant: tenant || tenantSlug, portal: 'player' });
    setUser(null);
    const res = await api.post('/api/auth/signup', {
      email,
      password,
      full_name: fullName || undefined,
      username: username || undefined,
      referral_code: referralCode || undefined,
      tenant_slug: tenant || tenantSlug || undefined,
    });
    const token = res.data.token || res.data.session_id;
    const nextTenant = res.data.user?.tenant_slug || tenant || tenantSlug;
    if (token) {
      storeSessionForTenantAliases(token, {
        canonicalTenant: nextTenant,
        currentTenant: tenant || resolveTenantSlug() || tenantSlug,
        portal: 'player',
      });
    }
    if (res.data.user?.tenant_slug) setTenantSlug(res.data.user.tenant_slug);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    const token = getStoredSessionToken();
    try { await api.post('/api/auth/logout'); } catch {}
    clearStoredSessionToken({ token });
    setUser(null);
  };

  const exchangeSession = async (sessionId) => {
    clearAllSessionTokens();
    storeSessionForTenantAliases(sessionId, {
      currentTenant: resolveTenantSlug(),
      portal: currentPortalFromPath(),
    });
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      exchangeSession, checkAuth, tenantSlug, setTenantSlug
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
